import { setMessage, setSuccessMessage } from 'lib/apps/notifications/actions/notifications';

const allowedMethods = [
  'get',
  'head',
  'post',
  'put',
  'patch',
  'del',
];

let isUserNotifiedAboutNetworkOutage = false;

// Maximum time in milliseconds defining how long the user's offline state should be hidden from them.
const TRANSPARENT_OFFLINE_TIMEOUT = 5000;

/**
 * Given dispatch and a Redux action, or an array of them, dispatches the actions.
 * @param {Function} dispatch dispatch Redux function
 * @param {Function|Function[]} functions A Redux action, or an array of them, to dispatch.
 * @param {*} args Arguments to be passed to Redux action(s)
 */
const dispatchFunctions = (dispatch, functions, ...args) =>
  functions && [].concat(functions).forEach(fn => {
    const funcResult = fn(...args);
    const returnedAction = funcResult && (typeof funcResult === 'function' || ('type' in funcResult));

    if (returnedAction) {
      dispatch(funcResult);
    }
  });

const queueRequestWhenOffline = (dispatch, isForegroundAction) => new Promise(resolve => {
  const shouldNotifyUserAboutNetworkOutage = isForegroundAction && !isUserNotifiedAboutNetworkOutage;

  // The next offline request should not cause notifications to pop up
  isUserNotifiedAboutNetworkOutage = true;

  // If the user is offline longer than 5 seconds, display a warning message.
  let didRequestTookLongerThanUsual = false;

  const handleNetworkNotComingBack = () => {
    didRequestTookLongerThanUsual = true;

    if (!shouldNotifyUserAboutNetworkOutage) {
      return;
    }

    dispatch(setMessage(
      'You’re still offline. Don’t worry, we’ll sync any changes with our website once you’re back online.'
    ));
  };

  const handleNetworkNotComingBackTimeout = setTimeout(handleNetworkNotComingBack, TRANSPARENT_OFFLINE_TIMEOUT);

  // Handle the request when the user is back online.
  const handleRequestWhenBackOnline = () => {
    // Reset the flag: if the next network outage occur, the user will be notified again.
    isUserNotifiedAboutNetworkOutage = false;

    // Cancel showing the message about the user being offline
    if (handleNetworkNotComingBackTimeout) {
      clearTimeout(handleNetworkNotComingBackTimeout);
    }

    window.removeEventListener('online', handleRequestWhenBackOnline);

    // Display a confirmation message if we have previously displayed a warning message.
    if (shouldNotifyUserAboutNetworkOutage && didRequestTookLongerThanUsual) {
      dispatch(setSuccessMessage(
        'You’re back online! We’ll now sync any changes you’ve made so that everything is up-to-date.'
      ));
    }

    resolve();
  };

  window.addEventListener('online', handleRequestWhenBackOnline);
});

const setOnBeforeUnload = value => {
  try {
    window.addEventListener('beforeunload', value);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(error);
  }
};

const removeOnBeforeUnload = value => {
  try {
    window.removeEventListener('beforeunload', value);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(error);
  }
};

/**
 * Creates a dispatchable Redux action which makes a network request, dispatching
 * defined actions for REQUEST, SUCCESS and ERROR.
 *
 * Example:
 *
 * const fetchBasket = fetchAction({
 *   actions: [FETCH_BASKET_REQUEST, FETCH_BASKET_SUCCESS, FETCH_BASKET_ERROR],
 *   url: '/api/v1/basket',
 * });
 *
 * @param {String[]} params.actions An array of three actions to dispatch - REQUEST, SUCCESS, and ERROR.
 * @param {String|Function} params.url Url to make a request to. Can also be a function which is provided with state and requestParams.
 * @param {Boolean} params.isUserAction Whether the action is user-made. If set to true, user will be notified about failures or temporary network outages.
 * @param {String} params.method A method to use while making a request. Can be 'get', 'head', 'post', 'put', 'patch', or 'del'.
 * @param {Function|Function[]} params.onRequest A Redux action, or an array of them, to dispatch on request.
 * @param {Function|Function[]} params.onSuccess A Redux action, or an array of them, to dispatch on success.
 * @param {Function|Function[]} params.onError A Redux action, or an array of them, to dispatch on error.
 */
export default ({
  actions,
  url,
  isUserAction = false,
  method = 'get',
  onRequest,
  onSuccess,
  onError,
}) => {
  if (!actions) {
    throw new Error('No action symbols provided. Expected an array of exactly 3 actions (request, success, failure).');
  } else if (!Array.isArray(actions) || actions.length !== 3) {
    throw new Error(
      'Invalid action symbols provided. Expected an array of exactly 3 actions (request, success, failure).'
    );
  }

  if (!url) {
    throw new Error('No URL provided. Expected a string or a function.');
  } else if (typeof url !== 'string' && typeof url !== 'function') {
    throw new Error('Invalid URL provided. Expected a string or a function.');
  }

  if (!method) {
    throw new Error(`No method provided. Expected one of: ${allowedMethods.join(', ')}.`);
  } else if (!allowedMethods.includes(method)) {
    throw new Error(`Invalid method provided. Expected one of: ${allowedMethods.join(', ')}.`);
  }

  const [REQUEST, SUCCESS, ERROR] = actions;

  /**
   * The action that will be dispatched by developers.
   *
   * Example:
   * dispatch(fetchBasket(requestParams, actionParams));
   */
  return (requestParams, actionParams) => (dispatch, getState, { network }) => {
    const urlString = typeof url === 'function' ? url(getState, requestParams, actionParams) : url;

    if (!urlString) {
      const noUrlErrorMsg = 'No URL provided.';
      const noUrlError = new Error(noUrlErrorMsg);
      dispatch({ type: ERROR, error: noUrlErrorMsg, requestParams });
      return Promise.reject(noUrlError);
    }

    const isClient = typeof window !== 'undefined';

    // User should be notified if action has failed or if there's a network outage only if they were the ones requesting
    const isForegroundAction = isClient && isUserAction;

    // Prevent the user from leaving the page while the request is pending
    const onBeforeUnloadAction = () => 'You have actions pending. If you leave now, these actions will be cancelled.';
    if (isForegroundAction) {
      setOnBeforeUnload(onBeforeUnloadAction);
    }

    dispatch({ type: REQUEST, requestParams, actionParams });
    dispatchFunctions(dispatch, onRequest, { requestParams, actionParams });

    const handleRequest = () => network[method](urlString, requestParams)
      .then(data => {
        dispatch({ type: SUCCESS, data, requestParams, actionParams });
        dispatchFunctions(dispatch, onSuccess, { data, requestParams, actionParams });
        if (isForegroundAction) {
          removeOnBeforeUnload(onBeforeUnloadAction);
        }
      })
      .catch(error => {
        dispatch({ type: ERROR, error, requestParams, actionParams });
        dispatchFunctions(dispatch, onError, { error, requestParams, actionParams });
        if (isForegroundAction) {
          removeOnBeforeUnload(onBeforeUnloadAction);
        }
      });

    // If the user is offline, queue the action until they are back online.
    if (isClient && !window.navigator.onLine) {
      return queueRequestWhenOffline(dispatch, isForegroundAction)
        .then(handleRequest);
    }

    return handleRequest();
  };
};
